import { IonAvatar, IonIcon } from "@ionic/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import EmptyAvatar from "@/assets/empty_avatar.svg";
import { Button } from "@/components/button";
import { api } from "@/lib/backend";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { Capacitor } from "@/lib/capacitor";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { cameraIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { queryClient } from "@/lib/providers/query";
import { queryOptions_signed_key_user } from "@/lib/query/file/file.get-signed-key";
import { cx } from "@/lib/style/cva.config";
import { logger } from "@/logger";

interface EditableAvatarProps {
  userId: string;
  imageKey: string;
  className?: string;
}

const TOAST_KEY = "update-profile-picture";

export function EditableAvatar({ userId, imageKey, className }: EditableAvatarProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const PreferredHaptics = usePreferredHaptics();
  const [imageError, setImageError] = useState(false);

  const {
    data: signedUrl,
    isLoading,
    refetch,
  } = useQuery({
    ...queryOptions_signed_key_user([userId, imageKey]),
    enabled: !!imageKey && !!userId && imageKey.trim() !== "",
  });

  // Reset error state when signed URL changes
  useEffect(() => {
    if (signedUrl?.url) {
      setImageError(false);
    }
  }, [signedUrl?.url]);

  const uploadMutation = useExtractedMutation({
    mutationKey: ["me", "profile-picture"],
    mutationFn: async (file: File) => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
      const allowedExtensions = ["jpg", "jpeg", "png", "gif", "webp"];

      if (!allowedExtensions.includes(fileExtension)) {
        throw new Error(t("actions:update-profile-picture.invalid-type"));
      }

      return api.me.updateProfilePicture({ file });
    },
    onMutate: () => {
      // Only dismiss previous toasts, don't show loading toast
      // Loading toasts cause a white overlay bug on iOS that blocks the UI
      toast.dismiss(TOAST_KEY);
    },
    onSuccess: (data) => {
      toast.dismiss(TOAST_KEY);
      toast.success(t("actions:update-profile-picture.success"), { id: TOAST_KEY });
      setImageError(false);

      try {
        // Update the "me" query cache with new image data instead of invalidating
        // This prevents Settings page re-render which causes ion-page-invisible to stick on iOS
        queryClient.setQueryData(["me", {}], data);

        // Only invalidate the file signed URL to get fresh signed URL for the new image
        queryClient.invalidateQueries({ queryKey: ["file", "user", userId] });
      } catch (error) {
        logger.error("Failed to update cache after profile picture update:", error);
      }
    },
    onError: (error) => {
      toast.dismiss(TOAST_KEY);
      toast.error(t("actions:update-profile-picture.error"), { id: TOAST_KEY });
      logger.error(error);
    },
  });

  const handleClick = () => {
    PreferredHaptics.impact();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate file size (e.g., 5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("actions:update-profile-picture.file-too-large"));
      return;
    }

    try {
      await uploadMutation.mutateAsync(file);
    } catch {
      // Error already handled by mutation's onError callback
    }

    event.target.value = "";
  };

  const handleImageError = () => {
    logger.error("Image failed to load:", signedUrl?.url);
    setImageError(true);
  };

  // Generate fallback image URL using UI Avatars
  const fallbackImageUrl = `https://ui-avatars.com/api/?background=fafafa&color=17254f&name=${encodeURIComponent(userId)}`;

  // Determine which image source to use
  const imageSource = useMemo(() => {
    if (isLoading) {
      return EmptyAvatar;
    }
    if (!signedUrl || imageError || !imageKey || imageKey.trim() === "") {
      return fallbackImageUrl;
    }
    // Don't add cache-busting to signed URLs as it invalidates the signature
    // Instead, rely on query invalidation to get fresh signed URLs
    return signedUrl.url;
  }, [signedUrl, imageError, isLoading, fallbackImageUrl, imageKey]);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      <IonAvatar
        className={cx("relative size-20 cursor-pointer ion-rounded-full", className)}
        onClick={handleClick}
      >
        <img
          key={signedUrl?.url || imageSource}
          src={imageSource}
          className="size-full rounded-full object-cover"
          alt="Profile"
          onError={handleImageError}
          referrerPolicy="no-referrer"
        />

        <Button
          fill="clear"
          className="absolute size-8 min-h-0 text-white opacity-100 part-icon:m-0 part-native:size-8 part-native:min-h-0 part-native:rounded-full part-native:bg-[#0066FF]/20 part-native:p-1.5 part-native:text-base part-native:shadow-[0px_4px_22px_0px_rgba(theme(colors.black.rgb)/.25)]"
          style={{
            bottom: "15%",
            left: "60%",
          }}
        >
          <IonIcon slot="icon-only" icon={cameraIcon} />
        </Button>
      </IonAvatar>
    </>
  );
}
